/**
 * @module app.basemaps
 */
import olLayerTile from 'ol/layer/Tile.js';
import {swisstopo, WMTS_TILE_GRID} from "./sources";
import {Z_INDEX} from "./constants";


/**
 * @const {string}
 */
export const WINTER_LAYER_PLACEHOLDER = '[WINTER_LAYER_PLACEHOLDER]';

/**
 * Format a WMTS tile URL that contains placeholders, such that winter layer is
 * used for resolutions 2, 2.5, and 5, and the regular layer otherwise.
 * If the URL does not contain any placeholders, it is not modified.
 * @param {string} url
 * @param {number} resolution
 */
export const resolutionDependentURL = (url, resolution) => {
  let layer;
  const z = WMTS_TILE_GRID.getZForResolution(resolution);
  // Z values 20, 21, 22, 23, 24, 25 correspond to resolutions 10, 5, 2.5, 2, 1.5, 1
  // Using z instead of resolution leads to correct behavior for
  // intermediate resolutions such as 9.42
  if (z >= 20 && z <= 25) {
    layer = 'ch.swisstopo.pixelkarte-farbe-winter';
  } else {
    layer = 'ch.swisstopo.pixelkarte-farbe';
  }
  return url
    .replace(WINTER_LAYER_PLACEHOLDER, layer);
};

class Controller {
  /**
   * @param {string} wmtsUrls
   * @param {string} swisstopoWmtsUrls
   * @param {!ngeo.statemanager.Location} ngeoLocation
   * @param {!angular.Scope} $scope
   * @ngInject
   */
  constructor(wmtsUrls, swisstopoWmtsUrls, ngeoLocation, $scope) {
    /**
     * @private
     */
    this.wmtsUrls_ = wmtsUrls;

    /**
     * @private
     */
    this.swisstopoWmtsUrls_ = swisstopoWmtsUrls;

    /**
     * @private
     */
    this.ngeoLocation_ = ngeoLocation;

    /**
     * @export
     * @type {!ol.Map}
     */
    this.map;

    /**
     * @export
     * @type {string}
     */
    this.season;

    /**
     * @type {string}
     * @export
     */
    this.baseLayerId;

    /**
     * @private
     * @type {Object.<string, ol.layer.Tile>}
     */
    this.baseLayers_ = null;

    $scope.$watch(() => this.season, () => {
      this.enableBaseLayer(this.baseLayerId);
    });

    /**
     * @private
     */
    this.scope_ = $scope;
  }

  /**
   * @param {string} layerId
   * @export
   */
  enableBaseLayer(layerId) {
    this.baseLayerId = layerId;

    if (this.season === 'winter' && layerId === 'pk') {
      layerId = 'pk-winter';
    }

    console.assert(this.baseLayers_);
    Object.entries(this.baseLayers_).forEach(([key, layer]) => {
      const visible = key === layerId;
      layer.setVisible(visible);
    });

    this.ngeoLocation_.updateParams({'bgLayer': this.baseLayerId});

    this.scope_.$root.$broadcast('base_layer_id_change', layerId);
  }

  $onInit() {
    this.setupBaselayers_();
  }

  /**
   * @private
   */
  setupBaselayers_() {
    // FIXME: to be able to debug https://jira.camptocamp.com/browse/GSCHM-1837
    const pixelKarteOpacity = location.search.includes('noBgOpacity') ? undefined : 0.85;

    // Replace format/layer placeholders with different values depending on resolution
    const winterTileLoadFunction = (imageTile, src) => {
      const resolution = this.map.getView().getResolution();
      console.assert(resolution);
      imageTile.getImage().src = resolutionDependentURL(src, resolution);
    };

    this.baseLayers_ = {
      'pk': new olLayerTile({
        visible: false,
        name: 'pixelkarte-farbe',
        opacity: pixelKarteOpacity,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.swisstopo.pixelkarte-farbe', 'current', 'jpeg'
        ),
        zIndex: Z_INDEX.BASEMAP,
      }),
      'pk-winter': new olLayerTile({
        visible: false,
        name: 'pk-winter',
        opacity: pixelKarteOpacity,
        source: swisstopo(
          this.swisstopoWmtsUrls_, WINTER_LAYER_PLACEHOLDER, 'current',
          'jpeg', winterTileLoadFunction
        ),
        zIndex: Z_INDEX.BASEMAP,
      }),
      'pkg': new olLayerTile({
        visible: false,
        name: 'pixelkarte-grau',
        opacity: pixelKarteOpacity,
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.swisstopo.pixelkarte-grau', 'current', 'jpeg'
        ),
        zIndex: Z_INDEX.BASEMAP,
      }),
      'lb': new olLayerTile({
        visible: false,
        name: 'swissimage',
        source: swisstopo(
          this.swisstopoWmtsUrls_, 'ch.swisstopo.swissimage', 'current', 'jpeg'
        ),
        zIndex: Z_INDEX.BASEMAP,
      }),
    };

    Object.values(this.baseLayers_).forEach((layer) => {
      this.map.addLayer(layer);
    });

    this.enableBaseLayer(this.ngeoLocation_.getParam('bgLayer') || 'pk');
  }
}

/**
 * @const {!angular.Component}
 */
const component = {
  bindings: {
    'map': '<',
    'season': '<',
  },
  controller: Controller,
  templateUrl: 'partials/basemaps.html'
};

export default angular.module('appBasemaps', []).component('appBasemaps', component);
